import { z } from 'zod/v3';

export const RecordPolicyReviewSchema = z.object({
  policyDocumentId: z.string().min(1),
  policyDocumentVersionId: z.string().min(1),
  requestId: z.string().uuid(),
  notes: z.string().trim().max(5000).optional(),
  participants: z.array(z.string().email()).max(100).optional(),
  fileIds: z.array(z.string().min(1)).max(10).optional(),
}).strict();
export type RecordPolicyReviewInput = z.infer<typeof RecordPolicyReviewSchema>;

export const PolicyReviewRecordSchema = z.object({
  id: z.string(),
  policyDocumentVersionId: z.string(),
  version: z.number(),
  source: z.enum(['approval', 'review']),
  decision: z.enum(['no_changes', 'changes_required']),
  reviewedAt: z.string(),
  reviewerName: z.string(),
  participants: z.array(z.string()),
  notes: z.string().nullable(),
  attachments: z.array(z.object({ id: z.string(), name: z.string(), url: z.string().nullable() })),
  recordedAt: z.string(),
  recordedByUserId: z.string().nullable(),
});
export type PolicyReviewRecordDTO = z.infer<typeof PolicyReviewRecordSchema>;

/** Report and auditor provenance retains file identity, never internal storage URLs. */
export const PolicyReviewProvenanceRecordSchema = PolicyReviewRecordSchema.extend({
  attachments: PolicyReviewRecordSchema.shape.attachments.transform((files) =>
    files.map((file) => ({ ...file, url: null }))),
});

/** Read only the provenance fields of the persisted report snapshot. */
export const PolicyReviewReportSnapshotSchema = z.object({
  generatedAt: z.string(),
  policies: z.array(z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    approvedVersionId: z.string().nullable(),
    reviewRecords: z.array(PolicyReviewProvenanceRecordSchema),
  })),
});

export const DownloadPolicyReviewAttachmentSchema = z.object({
  auditCycleId: z.string().min(1),
  source: z.enum(['policy', 'report', 'document']),
  documentId: z.string().min(1).optional(),
  sourceId: z.string().min(1),
  reviewId: z.string().min(1),
  fileId: z.string().min(1),
});
export type DownloadPolicyReviewAttachmentInput = z.infer<typeof DownloadPolicyReviewAttachmentSchema>;

/** Action target from the current policy projection; null means review is unavailable. */
export interface PolicyReviewTargetDTO {
  versionId: string;
  version: number;
}
